import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, ModalHeader, ModalBody, ModalFooter } from '@/components/common';
import type { FrontlineUserAdminDto } from '@/api/types';
import { useUpdateFrontlineEmail } from '@/hooks/useFrontlineUsersAdmin';
import { useSsoDomains } from '@/api/recognition';
import { esCorreoCorporativo } from '@/views/recognition/access/accessErrors';
import { frontlineErrorKey } from './frontlineErrors';
import { accesoPorSso, personalEsCorporativo } from './correosFicha';
import { PasswordSection } from './PasswordSection';

const TITLE_ID = 'gu-edit-title';

/**
 * El mismo criterio que valida la maqueta (algo@dominio.xx, sin espacios); el servidor repite la comprobación
 * (`EmailFormatPolicy`). Se escribe sin expresión regular para no tener backtracking con entradas largas.
 */
function isValidEmailFormat(value: string): boolean {
  if (/\s/.test(value)) return false;
  const at = value.indexOf('@');
  if (at < 1 || at !== value.lastIndexOf('@')) return false;
  const domain = value.slice(at + 1);
  const dot = domain.lastIndexOf('.');
  return dot > 0 && domain.length - dot - 1 >= 2;
}

/** Texto de ayuda bajo el campo personal, según lo que se haya tecleado. */
function personalHelpKey(s: { changed: boolean; hasValue: boolean; loginEsCorp: boolean; verificado: boolean }): string {
  if (s.changed) return s.hasValue ? 'frontlineUsers.helpChange' : 'frontlineUsers.helpClear';
  if (s.loginEsCorp) return 'frontlineUsers.personalReplaceHelp';
  return s.verificado ? 'frontlineUsers.helpVerified' : 'frontlineUsers.helpDefault';
}

/** Texto de ayuda bajo el campo corporativo: quien entra por SSO no tiene nada más que editar aquí, y se dice por qué. */
function corpHelpKeyFor(s: { delAd: boolean; sinPersonal: boolean; loginEsCorp: boolean }): string {
  if (s.delAd) return 'frontlineUsers.err.directory';
  if (s.sinPersonal) return 'frontlineUsers.ssoAccessHelp';
  return s.loginEsCorp ? 'frontlineUsers.corpLoginHelp' : 'frontlineUsers.corpHelp';
}

interface Props {
  user: FrontlineUserAdminDto;
  onClose: () => void;
  onSaved: (message: string) => void;
  onError: (message: string) => void;
}

/**
 * Edita el correo personal (el de acceso). El corporativo se muestra bloqueado: viene de SAP. Al cambiar el
 * correo, el servidor reinicia la verificación («No enviado») y descarta los enlaces del correo anterior.
 */
export function EditEmailModal({ user, onClose, onSaved, onError }: Readonly<Props>) {
  const { t } = useTranslation();
  const update = useUpdateFrontlineEmail();
  const ssoDomains = useSsoDomains();
  // Cuenta del AD (personal de la empresa): todo el modal es de solo lectura.
  const delAd = user.estado === 'DelDirectorio';
  const original = user.correoPersonal ?? '';
  // Mismo criterio que la lista: si el correo con el que entra es de la empresa, se muestra en el campo corporativo
  // (bloqueado) y el campo personal arranca vacío; vacío = conservarlo. Escribir otro correo lo sustituye.
  const loginEsCorp = personalEsCorporativo(user, ssoDomains.data);
  // Ficha del padrón sin reclamar que entra por SSO: no admite correo personal ni enlace (el servidor también lo rechaza).
  const sinPersonal = accesoPorSso(user, ssoDomains.data);
  // Staff = entra con su cuenta corporativa (AD o SSO): el correo personal y la contraseña no aplican, así que no se muestran.
  // Quien entra con correo y contraseña (Frontline, o una cuenta reclamada) los sigue viendo.
  const staff = delAd || sinPersonal;
  const base = loginEsCorp ? '' : original;
  // `null` = la persona aún no ha tecleado: se muestra la base aunque los dominios SSO lleguen después de abrir el modal.
  const [edited, setEdited] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const value = edited ?? base;
  const trimmed = value.trim();
  const changed = trimmed.toLowerCase() !== base.toLowerCase();

  // El campo corporativo: el de SuccessFactors; si la persona entra con el de la empresa (o es del AD), ese mismo.
  const corpShown = (loginEsCorp || delAd ? user.correoCorporativo || user.correoPersonal : user.correoCorporativo)
    || t(user.sinLicencia ? 'frontlineUsers.noLicenseCorp' : 'frontlineUsers.corpNone');

  const corpHelpKey = corpHelpKeyFor({ delAd, sinPersonal, loginEsCorp });
  const help = t(personalHelpKey({ changed, hasValue: !!trimmed, loginEsCorp, verificado: user.estado === 'Verificado' }));

  let helpClass = 'gu-help';
  if (error) helpClass += ' gu-help--error';
  else if (changed) helpClass += ' gu-help--warn';

  const save = async () => {
    if (trimmed && !isValidEmailFormat(trimmed)) {
      setError(t('frontlineUsers.err.invalid'));
      return;
    }
    if (!changed) { onClose(); return; }
    try {
      await update.mutateAsync({ id: user.id, correoPersonal: trimmed || null });
      onSaved(t('frontlineUsers.saved', { name: user.nombre }));
      onClose();
    } catch (err) {
      setError(t(frontlineErrorKey(err)));
    }
  };

  return (
    <Modal open onClose={onClose} maxWidth={520} labelledBy={TITLE_ID}>
      <ModalHeader onClose={onClose}><h2 id={TITLE_ID}>{t('frontlineUsers.editTitle', { name: user.nombre })}</h2></ModalHeader>
      <ModalBody>
        <div className="gu-ficha">
          {user.nomina && <span className="gu-tag gu-tag--payroll">{user.nomina}</span>}
          {user.ou && <span className="gu-tag gu-tag--ou">{user.ou}</span>}
          {user.sitio && <span className="gu-tag gu-tag--site">{user.sitio}</span>}
          {user.sinLicencia && <span className="gu-tag gu-tag--nolicense">{t('frontlineUsers.noLicenseTag')}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="gu-corp">{t('frontlineUsers.colCorp')}</label>
          <div className="gu-readonly">
            <input id="gu-corp" type="text" readOnly className="gu-input" aria-describedby="gu-corp-help"
              value={corpShown} />
            <i className="fas fa-lock" aria-hidden="true" />
          </div>
          <p className="gu-help" id="gu-corp-help">{t(corpHelpKey)}</p>
        </div>

        {!staff && (<>
        <div className="form-group">
          <label htmlFor="gu-personal">{t('frontlineUsers.colPersonal')}</label>
          <input
            id="gu-personal"
            type="email"
            className={error ? 'gu-input gu-invalid' : 'gu-input'}
            value={value}
            placeholder="correo@personal.com"
            aria-describedby="gu-personal-help"
            aria-invalid={error ? true : undefined}
            onChange={e => { setEdited(e.target.value); setError(null); }}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); void save(); } }}
            autoFocus
          />
          <p className={helpClass} id="gu-personal-help" role={error ? 'alert' : undefined}>
            {error ?? help}
          </p>
          {changed && esCorreoCorporativo(original, ssoDomains.data) && (<p className="gu-help gu-help--warn" role="note">{t('frontlineUsers.ssoRiskWarn')}</p>)}
        </div>

        <PasswordSection user={user} onDone={onSaved} onError={onError} />
        </>)}
      </ModalBody>
      <ModalFooter>
        <button type="button" className="btn btn-secondary" onClick={onClose}>{t('common.cancel')}</button>
        <button type="button" className="btn btn--primary" onClick={() => void save()} disabled={staff || update.isPending}>
          <i className={`fas ${update.isPending ? 'fa-spinner fa-spin' : 'fa-save'}`} /> {t('common.save')}
        </button>
      </ModalFooter>
    </Modal>
  );
}
